/* ==========================================================================
   Early-access signup page (sections/early-access-signup.liquid)

   Same flow as assets/giveaway.js, but the lists come from the section's
   settings instead of being hard-coded, so each drop can point at its own
   Klaviyo list without touching code.

   On submit, in this order:
     1. Subscribe to the early-access list and wait for Klaviyo to confirm.
     2. Only then, if a main list is set, subscribe to it too, so signups also
        get the newsletter. Main-list membership triggers the welcome series.
     3. Post Shopify's native customer form so the Shopify customer record is
        still created and tagged.
   If step 1 fails or times out, step 2 is skipped and the Shopify signup
   still goes through, so the email is never lost.

   The "welcome back" popup shows only when the EXACT email typed has been
   submitted from this device before (Shopify blocks real cross-device
   lookups for privacy). Dedup is server-side: Shopify updates an existing
   customer and Klaviyo matches on email.
   ========================================================================== */
(function () {
  'use strict';

  var KLAVIYO_REVISION = '2024-10-15';
  var KLAVIYO_TIMEOUT_MS = 2500;

  var section = document.querySelector('.ea-signup');
  if (!section) return;

  var EMAILS = 'eas_emails';      // JSON array of normalized emails submitted on this device
  var PENDING = 'eas_pending';    // {email, returning} stashed just before submit, read after reload

  var cfgEl = section.querySelector('[data-gv-config]');
  var cfg = {};
  try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { cfg = {}; }

  var modal = section.querySelector('[data-gv-modal]');
  var form = section.querySelector('#easForm');
  var emailInput = section.querySelector('#easEmail');

  function read(key) { try { return localStorage.getItem(key); } catch (e) { return null; } }
  function store(key, val) { try { localStorage.setItem(key, val); } catch (e) {} }
  function drop(key) { try { localStorage.removeItem(key); } catch (e) {} }

  function normalize(email) { return (email || '').trim().toLowerCase(); }
  function getEmails() {
    try { return JSON.parse(read(EMAILS)) || []; } catch (e) { return []; }
  }
  function rememberEmail(email) {
    var list = getEmails();
    if (email && list.indexOf(email) === -1) {
      list.push(email);
      store(EMAILS, JSON.stringify(list));
    }
  }
  function hasEmail(email) { return email && getEmails().indexOf(email) !== -1; }

  /* Resolves true only if Klaviyo accepted the request; false on error, a
     non-2xx response, or a timeout. Never rejects. */
  function subscribeToList(listId, email) {
    if (!cfg.klaviyoCompanyId || !listId || !email || !window.fetch) {
      return Promise.resolve(false);
    }

    var payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: cfg.customSource || 'Early access page',
          profile: { data: { type: 'profile', attributes: { email: email } } }
        },
        relationships: { list: { data: { type: 'list', id: listId } } }
      }
    };

    var request = fetch(
      'https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(cfg.klaviyoCompanyId),
      {
        method: 'POST',
        keepalive: true,           // lets the request finish after the page unloads
        headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
        body: JSON.stringify(payload)
      }
    ).then(function (res) {
      return !!(res && res.ok);    // Klaviyo returns 202 Accepted
    }).catch(function () {
      return false;
    });

    return Promise.race([
      request,
      new Promise(function (resolve) { setTimeout(function () { resolve(false); }, KLAVIYO_TIMEOUT_MS); })
    ]);
  }

  function openModal(title, msg, emoji) {
    if (!modal) return;
    modal.querySelector('[data-gv-modal-title]').textContent = title || '';
    modal.querySelector('[data-gv-modal-msg]').textContent = msg || '';
    var em = modal.querySelector('[data-gv-modal-emoji]');
    if (em) em.textContent = emoji || '🎉';
    modal.hidden = false;
    document.body.style.overflow = 'hidden';
    var closeBtn = modal.querySelector('.gv-modal__cta');
    if (closeBtn) closeBtn.focus();
  }
  function closeModal() {
    if (!modal) return;
    modal.hidden = true;
    document.body.style.overflow = '';
  }

  section.querySelectorAll('[data-gv-modal-close]').forEach(function (el) {
    el.addEventListener('click', closeModal);
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && modal && !modal.hidden) closeModal();
  });

  /* ----- After Shopify's success reload ----- */
  var url = new URL(window.location.href);
  var posted = url.searchParams.get('customer_posted') === 'true' ||
               !!section.querySelector('[data-gv-server-success]');

  if (posted) {
    var pending = {};
    try { pending = JSON.parse(read(PENDING)) || {}; } catch (e) { pending = {}; }

    if (pending.email) rememberEmail(pending.email);
    drop(PENDING);

    if (pending.returning) {
      openModal(cfg.welcomeBackTitle, cfg.welcomeBack, '👋');
    } else {
      openModal(cfg.successNewTitle, cfg.successNew, '🎉');
    }

    // Clean the success params out of the URL so a refresh doesn't re-pop.
    if (window.history && window.history.replaceState) {
      url.searchParams.delete('customer_posted');
      url.searchParams.delete('form_type');
      window.history.replaceState({}, document.title, url.pathname + (url.search || '') + url.hash);
    }
    var inline = section.querySelector('[data-gv-server-success]');
    if (inline) inline.style.display = 'none';
  }

  /* ----- On submit: early-access list, then main list, then Shopify ----- */
  if (form) {
    var releasing = false;

    form.addEventListener('submit', function (e) {
      if (releasing) return;      // our own re-submit — let it go to Shopify
      e.preventDefault();

      var email = normalize(emailInput && emailInput.value);
      store(PENDING, JSON.stringify({ email: email, returning: hasEmail(email) }));

      subscribeToList(cfg.klaviyoListId, email).then(function (entered) {
        // keepalive means this finishes even though we navigate immediately.
        if (entered && cfg.klaviyoMainListId) subscribeToList(cfg.klaviyoMainListId, email);

        releasing = true;
        form.submit();
      });
    });
  }
})();
